<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleDeedFeedbackBranches from './PostSaleDeedFeedbackBranches.vue';
import PostSaleDeedFeedbackChoiceGroup from './PostSaleDeedFeedbackChoiceGroup.vue';

const props = defineProps({
  catalog: { type: Object, default: () => ({}) },
  process: { type: Object, required: true },
  proof: { type: Object, default: null },
  showBack: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
  uploading: { type: Boolean, default: false },
});
const emit = defineEmits(['back', 'submit', 'upload']);
const { t } = useI18n();
const form = ref({});

const blank = process => ({
  aconteceu: '',
  pagamento: '',
  resultado: '',
  nova_data: '',
  motivo_pagamento: '',
  motivo_queda: '',
  deed: {
    cartorio_id: process.deed?.cartorio_id,
    local_escritura: process.deed?.cartorio_id || '',
    data_escritura: process.deed?.data_escritura || '',
    hora_escritura: process.deed?.hora_escritura || '',
    nome_cartorio: '',
    cep_escritura: process.deed?.cep || '',
    endereco_escritura: process.deed?.logradouro || '',
    numero_escritura: process.deed?.numero || '',
    unidade_escritura: process.deed?.unidade || '',
    complemento_escritura: process.deed?.complemento || '',
    cidade_escritura: process.deed?.cidade || '',
    estado_escritura: process.deed?.uf || '',
    bairro_escritura: process.deed?.bairro || '',
  },
});

watch(
  () => props.process,
  process => {
    form.value = blank(process);
  },
  { immediate: true }
);
const question = id =>
  (props.catalog.questions || []).find(item => Number(item.id) === id) || {
    answers: [],
  };
const hasPayment = computed(() => Number(props.process.pg_na_escritura) === 1);
const deedReady = computed(() => {
  const deed = form.value.deed;
  return [
    'data_escritura',
    'hora_escritura',
    'local_escritura',
    'cep_escritura',
    'endereco_escritura',
    'numero_escritura',
    'cidade_escritura',
    'estado_escritura',
    'bairro_escritura',
  ].every(key => deed[key]);
});
const canSubmit = computed(() => {
  if (!form.value.aconteceu) return false;
  if (form.value.aconteceu === '1' && hasPayment.value) {
    if (!form.value.pagamento) return false;
    return form.value.pagamento === '3'
      ? Boolean(form.value.nova_data && form.value.motivo_pagamento)
      : Boolean(props.proof?.id);
  }
  if (form.value.aconteceu !== '2' || !form.value.resultado)
    return form.value.aconteceu === '1';
  return form.value.resultado === '5'
    ? Boolean(form.value.motivo_queda)
    : deedReady.value;
});

const submit = () => {
  const responses = [
    { question_id: 1, answer_id: Number(form.value.aconteceu) },
  ];
  if (form.value.aconteceu === '1' && hasPayment.value) {
    responses.push({
      question_id: 2,
      answer_id: Number(form.value.pagamento),
      motivo: form.value.motivo_pagamento,
      nova_data: form.value.nova_data,
      document_artifact_id: props.proof?.id,
    });
  }
  if (form.value.aconteceu === '2') {
    responses.push({
      question_id: 3,
      answer_id: Number(form.value.resultado),
      motivo: form.value.motivo_queda,
      deed: form.value.deed,
    });
  }
  emit('submit', responses);
};
</script>

<template>
  <section class="grid gap-0">
    <div>
      <h2
        class="mb-10 font-redHatDisplay text-2xl font-bold leading-9 text-lais-text"
      >
        {{
          t('POST_SALE.DEED_FEEDBACK.PROCESS_TITLE', {
            date: process.data_escritura,
            address: process.logradouro,
          })
        }}
      </h2>
      <p class="mb-10 text-xl leading-6 text-lais-text">
        {{
          t('POST_SALE.DEED_FEEDBACK.QUESTION_TITLE', {
            name: process.name,
            question: question(1).prompt,
          })
        }}
      </p>
    </div>
    <PostSaleDeedFeedbackChoiceGroup
      v-model="form.aconteceu"
      :options="question(1).answers"
    />
    <PostSaleDeedFeedbackBranches
      :form="form"
      :payment-question="question(2)"
      :process="process"
      :proof="proof"
      :result-question="question(3)"
      :uploading="uploading"
      @update:form="form = $event"
      @upload="emit('upload', $event)"
    />
    <footer
      data-testid="post-sale-deed-feedback-actions"
      class="fixed bottom-[33px] left-1/2 z-[99] flex w-[calc(100%-2.5rem)] max-w-[725px] -translate-x-1/2 items-center gap-3 bg-white py-5 sm:w-[calc(100%-5rem)]"
      :class="showBack ? 'justify-between' : 'justify-end'"
    >
      <LaisPrimaryButton
        v-if="showBack"
        data-testid="post-sale-deed-feedback-back"
        size="lg"
        variant="link"
        @click="emit('back')"
      >
        {{ t('POST_SALE.DEED_FEEDBACK.BACK') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        data-testid="post-sale-deed-feedback-submit"
        :disabled="!canSubmit"
        :is-loading="submitting"
        size="lg"
        @click="submit"
      >
        {{ t('POST_SALE.DEED_FEEDBACK.SUBMIT') }}
        <span
          v-if="!submitting"
          class="i-lucide-check ml-2 inline-block size-4 align-middle"
          aria-hidden="true"
        />
      </LaisPrimaryButton>
    </footer>
  </section>
</template>
